import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {APPROVAL_STATUS, APPROVAL_TYPE, ApprovalStatus} from '../../../../constants';
import {trackEvent} from './../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import Util from '../../../shared/util/util';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import ForecastTooltip from '../../../shared/components/tooltips/ForecastTooltip';
import {createToast} from '../../../shared/components/toasts/another-toast/toaster';
import UpdateApprovalStatusMutation from '../../../../mutations/UpdateApprovalStatusMutation';
import {ConfirmationModalPrimaryButton} from '../../../shared/components/modals/general/ConfirmationModal';

const submitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	lastWorkingDayInWeek,
	usingTimeOffApproval,
}) => {
	trackEvent('Submit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const today = Moment();
	const submitDate = !isWeekView
		? currentViewingDate
		: today.isSame(currentViewingDate, 'week')
		? today
		: lastWorkingDayInWeek;
	showModal({
		type: MODAL_TYPE.SUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: submitDate,
		lockedDate,
		usingTimeOffApproval,
	});
};

const resubmitTimesheets = ({nonViewerPersonSelected, selectedPerson, isWeekView, currentViewingDate}) => {
	trackEvent('Resubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const rangeStart = isWeekView ? currentViewingDate.clone().startOf('week') : currentViewingDate;
	const rangeEnd = isWeekView ? currentViewingDate.clone().endOf('week') : currentViewingDate;
	Util.CommitMutation(UpdateApprovalStatusMutation, {
		personIds: [selectedPerson.id],
		approvalType: APPROVAL_TYPE.RESUBMIT,
		startYear: rangeStart.year(),
		startMonth: rangeStart.month() + 1,
		startDay: rangeStart.date(),
		endYear: rangeEnd.year(),
		endMonth: rangeEnd.month() + 1,
		endDay: rangeEnd.date(),
	});
};

const unsubmitTimesheets = ({
	nonViewerPersonSelected,
	selectedPerson,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
}) => {
	trackEvent('Unsubmit Timesheets Bottom', 'Clicked', {
		timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
	});
	const unsubmitDate = !isWeekView ? currentViewingDate : currentViewingDate.clone().startOf('week');
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		selectedPersonName: nonViewerPersonSelected ? selectedPerson.firstName : undefined,
		selectedPersonId: selectedPerson.id,
		currentViewingDate: unsubmitDate,
		companyLockedDate,
		lockedDate,
	});
};

type TimesheetsApprovalButtonsProps = {
	selectedPerson; // TimesheetsPage_viewer['company']['person']
	nonViewerPersonSelected: boolean;
	usingTimeApproval: boolean;
	usingTimeOffApproval: boolean;
	isWeekView: boolean;
	currentViewingDate: Moment.Moment;
	lockedDate?: Moment.Moment;
	companyLockedDate?: Moment.Moment;
	isFullyLocked: boolean;
	lastWorkingDayInWeek;
	periodTimeApprovalStatus?: ApprovalStatus;
};
export const TimesheetsApprovalButtons = ({
	selectedPerson,
	nonViewerPersonSelected,
	usingTimeApproval,
	usingTimeOffApproval,
	isWeekView,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	isFullyLocked,
	lastWorkingDayInWeek,
	periodTimeApprovalStatus,
}: TimesheetsApprovalButtonsProps): JSX.Element | null => {
	const {formatMessage} = useIntl();

	const handleConfirmTimeOff = (selectedPersonId: number) => {
		trackEvent('Submit Time Off Modal Save Button', 'Clicked');
		const onSuccess = res => {
			const personRes = res.updateApprovalStatus.persons[0];
			if (personRes) {
				createToast({
					duration: 2500,
					message: formatMessage({id: 'time_approval.submit_time_off_toast'}),
				});
			}
		};

		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.SUBMIT_TIME_OFF,
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const handleCloseTimeOffModal = () => {
		trackEvent('Submit Time Off Modal Cancel Button', 'Clicked');
	};

	const openSubmitTimeOffModal = ({nonViewerPersonSelected, selectedPerson}) => {
		trackEvent('Submit Time Off Bottom', 'Clicked', {
			timeRegPerson: nonViewerPersonSelected ? selectedPerson.email : 'myself',
		});

		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'time_approval.submit_time_off'}),
			description: formatMessage({id: 'time_off_approval.submit_timesheets_info'}),
			autoClosePositive: true,
			autoCloseNegative: true,
			callbackPositive: () => handleConfirmTimeOff(selectedPerson.id),
			callbackNegative: handleCloseTimeOffModal,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.SAVE,
		});
	};

	const isCompanyLocked = useMemo(() => {
		if (companyLockedDate) {
			return isWeekView
				? companyLockedDate.isSameOrAfter(currentViewingDate.clone().endOf('week'), 'd')
				: companyLockedDate.isSameOrAfter(currentViewingDate, 'd');
		}
		return false;
	}, [isWeekView, companyLockedDate, currentViewingDate]);

	const isWorkdaysLocked = useMemo(() => {
		if (lockedDate && lastWorkingDayInWeek) {
			return lockedDate.isSameOrAfter(lastWorkingDayInWeek, 'd');
		}
		return false;
	}, [isWeekView, lockedDate, lastWorkingDayInWeek]);

	const messageIdSuffix = (usingTimeOffApproval ? '_and_time_off' : '') + '_info';

	if (!usingTimeApproval && !usingTimeOffApproval) return null;

	const hasNewStatuses = hasFeatureFlag('timesheet_approval_new_statuses');

	const isSubmitOrApproved =
		hasNewStatuses &&
		(periodTimeApprovalStatus === APPROVAL_STATUS.APPROVED || periodTimeApprovalStatus === APPROVAL_STATUS.INVOICED);

	const shouldShowResubmit = hasNewStatuses && periodTimeApprovalStatus === APPROVAL_STATUS.DECLINED;

	const shouldShowUnsubmit = hasNewStatuses
		? periodTimeApprovalStatus === APPROVAL_STATUS.SUBMITTED ||
		  (periodTimeApprovalStatus === APPROVAL_STATUS.NOT_SUBMITTED && isWorkdaysLocked)
		: isWorkdaysLocked && periodTimeApprovalStatus !== APPROVAL_STATUS.DECLINED;

	return usingTimeApproval ? (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.MONO}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						disabled={isFullyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
			{isSubmitOrApproved ? null : shouldShowResubmit ? (
				<Button
					emphasis={EMPHASIS.HIGH}
					onPress={() =>
						resubmitTimesheets({nonViewerPersonSelected, selectedPerson, isWeekView, currentViewingDate})
					}
					data-cy={'resubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage({id: 'time_approval.resubmit_names_timesheets'}, {name: selectedPerson.firstName})
						: formatMessage({id: 'time_approval.resubmit_timesheets'})}
				</Button>
			) : shouldShowUnsubmit ? (
				<Button
					emphasis={EMPHASIS.HIGH}
					onPress={() =>
						unsubmitTimesheets({
							nonViewerPersonSelected,
							selectedPerson,
							isWeekView,
							currentViewingDate,
							lockedDate,
							companyLockedDate,
						})
					}
					disabled={isCompanyLocked}
					data-cy={'unsubmit-timesheets-button'}
				>
					{nonViewerPersonSelected
						? formatMessage(
								{
									id: 'time_approval.unsubmit_names_timesheets',
								},
								{
									name: selectedPerson.firstName,
								}
						  )
						: formatMessage({
								id: 'time_approval.unsubmit_timesheets',
						  })}
				</Button>
			) : (
				<ForecastTooltip
					content={
						hasFeatureFlag('pto_timesheet_allocation_linking')
							? nonViewerPersonSelected
								? formatMessage(
										{
											id: 'time_approval.submit_names_timesheets' + messageIdSuffix,
										},
										{
											name: selectedPerson.firstName,
										}
								  )
								: formatMessage({
										id: 'time_approval.submit_timesheets' + messageIdSuffix,
								  })
							: undefined
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							submitTimesheets({
								nonViewerPersonSelected,
								selectedPerson,
								isWeekView,
								currentViewingDate,
								lockedDate,
								lastWorkingDayInWeek,
								usingTimeOffApproval,
							})
						}
						disabled={isFullyLocked}
						data-cy={'submit-timesheets-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_timesheet',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_timesheet',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	) : (
		<>
			{usingTimeOffApproval && (
				<ForecastTooltip
					content={
						nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_off_approval.submit_names_timesheets_info',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_off_approval.submit_timesheets_info',
							  })
					}
				>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={() =>
							openSubmitTimeOffModal({
								nonViewerPersonSelected,
								selectedPerson,
							})
						}
						disabled={isFullyLocked}
						data-cy={'submit-time-off-button'}
					>
						{nonViewerPersonSelected
							? formatMessage(
									{
										id: 'time_approval.submit_names_time_off',
									},
									{
										name: selectedPerson.firstName,
									}
							  )
							: formatMessage({
									id: 'time_approval.submit_time_off',
							  })}
					</Button>
				</ForecastTooltip>
			)}
		</>
	);
};
